import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/apiService';
import API_CONFIG from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar se há token salvo no localStorage ao carregar a aplicação
  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('user');

        if (savedToken && savedUser) {
          setToken(savedToken);
          const savedUserData = JSON.parse(savedUser);
          setUser(savedUserData);
          apiService.setToken(savedToken);
          
          // Buscar perfil atualizado com menus permitidos
          try {
            const profileResult = await apiService.authenticatedRequest(API_CONFIG.ENDPOINTS.AUTH.PROFILE, {
              method: 'GET'
            });
            
            if (profileResult.success && profileResult.data) {
              // Atualizar user com allowedMenus
              const updatedUser = {
                ...savedUserData,
                allowedMenus: profileResult.data.allowedMenus || savedUserData.allowedMenus || []
              };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          } catch (profileError) {
            // Se o erro for 401, limpar dados e redirecionar
            if (profileError.message.includes('401') || profileError.message.includes('Token') || 
                profileError.message.includes('Sessão expirada')) {
              logout();
              window.location.href = '/adm_i9/login';
              return;
            }
            // Continuar mesmo se falhar ao buscar perfil (mas não se for erro de autenticação)
          }
        }
      } catch (error) {
        console.error('❌ [AUTH] Erro ao inicializar autenticação:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Função de login
  const login = async (username, password) => {
    try {
      const result = await apiService.login(username, password);
      
      if (result.success) {
        const { token: authToken, user: userData } = result.data;
        
        // Buscar perfil completo com menus permitidos
        try {
          const profileResult = await apiService.authenticatedRequest(API_CONFIG.ENDPOINTS.AUTH.PROFILE || '/api/auth/profile', {
            method: 'GET'
          });
          
          if (profileResult.success && profileResult.data) {
            // Atualizar userData com allowedMenus
            userData.allowedMenus = profileResult.data.allowedMenus || userData.allowedMenus || [];
          }
        } catch (profileError) {
          console.warn('Erro ao buscar perfil do usuário:', profileError);
          // Continuar mesmo se falhar ao buscar perfil
        }
        
        // Salvar no estado
        setToken(authToken);
        setUser(userData);
        
        // Salvar no localStorage
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Erro no login' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Erro de conexão com o servidor' };
    }
  };

  // Função de logout
  const logout = () => {
    apiService.logout();
    setToken(null);
    setUser(null);
  };

  // Função para verificar se o usuário está autenticado
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  // Função para fazer requisições autenticadas
  const apiRequest = async (url, options = {}) => {
    if (!token) {
      logout();
      window.location.href = '/adm_i9/login';
      throw new Error('Usuário não autenticado');
    }

    try {
      return await apiService.authenticatedRequest(url, options);
    } catch (error) {
      // Se o erro for de autenticação, fazer logout e redirecionar
      if (error.message.includes('401') || error.message.includes('Token') || 
          error.message.includes('Sessão expirada') || error.message.includes('Token expirado') ||
          error.message.includes('Token inválido')) {
        logout();
        // O redirecionamento já será feito pelo apiService, mas garantimos aqui também
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/adm_i9/login';
        }
        throw new Error('Sessão expirada. Redirecionando para login...');
      }
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    apiRequest
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};



